"use client";

import * as React from "react";
import { LuArrowLeft, LuArrowRight } from "react-icons/lu";
import { cn } from "../../lib/utils";
import { HERO_BANNER_STYLES } from "./constants";
import type { HeroBannerProps } from "./types";

export const HeroBanner = React.forwardRef<HTMLElement, HeroBannerProps>(
  (
    {
      className,
      backgroundImageUrls,
      title,
      subtitle,
      logoImageUrl,
      ...props
    },
    ref
  ) => {
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const hasMultipleImages = backgroundImageUrls.length > 1;

    const handlePrevious = () => {
      setCurrentIndex((index) =>
        index === 0 ? backgroundImageUrls.length - 1 : index - 1
      );
    };

    const handleNext = () => {
      setCurrentIndex((index) =>
        index === backgroundImageUrls.length - 1 ? 0 : index + 1
      );
    };

    return (
      <section
        ref={ref}
        className={cn(HERO_BANNER_STYLES.root, className)}
        {...props}
      >
        <div className={HERO_BANNER_STYLES.imageWrapper}>
          <img
            src={backgroundImageUrls[currentIndex]}
            alt={title}
            className={HERO_BANNER_STYLES.image}
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className={HERO_BANNER_STYLES.overlay} />

          {hasMultipleImages && (
            <>
              <button
                onClick={handlePrevious}
                className={cn(
                  HERO_BANNER_STYLES.navButton,
                  HERO_BANNER_STYLES.prevButton
                )}
                aria-label="Previous slide"
                type="button"
              >
                <LuArrowLeft className={HERO_BANNER_STYLES.navIcon} />
              </button>

              <button
                onClick={handleNext}
                className={cn(
                  HERO_BANNER_STYLES.navButton,
                  HERO_BANNER_STYLES.nextButton
                )}
                aria-label="Next slide"
                type="button"
              >
                <LuArrowRight className={HERO_BANNER_STYLES.navIcon} />
              </button>
            </>
          )}

          <div className={HERO_BANNER_STYLES.content}>
            {logoImageUrl && (
              <img
                src={logoImageUrl}
                alt={`${title} logo`}
                className={HERO_BANNER_STYLES.logo}
                loading="lazy"
                decoding="async"
              />
            )}
            <h1 className={HERO_BANNER_STYLES.title}>{title}</h1>
            {subtitle && (
              <p className={HERO_BANNER_STYLES.subtitle}>{subtitle}</p>
            )}
          </div>
        </div>
      </section>
    );
  }
);

HeroBanner.displayName = "HeroBanner";
